import type { ReactNode } from 'react';
import { getDestination } from '@/features/interactions/addToCalendar/destinations/registry';
import type { DestinationModule } from '@/features/interactions/addToCalendar/destinations/types';
import type { PlatformTypes } from '@/types/gql.generated';
import { Button, Flex, Text } from '@/ui';
import { getName } from '@/utils/platformLib';

// One-line "how this destination behaves" note shown under the picker, led by a
// pill naming the sync type. Both are declared by the destination module.
// Weblink (per-event) destinations get bespoke inline copy instead, so they
// declare no description.
type SyncKind = NonNullable<DestinationModule['syncKind']>;

// The chip presentation for each declared sync kind — surface styling, so it
// stays here rather than on the modules. The label names the destination
// because "Syncs" alone reads as though the calendar syncs in either
// direction; the target says which way it goes.
const SYNC_STYLE: Record<
  SyncKind,
  {
    label: (target: string, preposition: 'to' | 'via') => string;
    color: string;
    bg: string;
  }
> = {
  live: {
    label: (target, preposition) => `Live sync ${preposition} ${target}`,
    color: 'magicPurple.600',
    bg: 'magicPurple.50',
  },
  sync: {
    label: (target, preposition) => `Syncs ${preposition} ${target}`,
    color: 'magicPurple.600',
    bg: 'magicPurple.50',
  },
  copy: {
    label: () => 'One-time',
    color: 'ink.muted',
    bg: 'surface.panel',
  },
};

// Shared so the weblink / ICS / default variants stay visually identical.
const Note = ({ children }: { children: ReactNode }) => (
  <Text color="ink.muted" fontSize="ah-status" lineHeight={1.4} mt={3}>
    {children}
  </Text>
);

// The four size props strip Chakra's default `md` button sizing, which pads
// visible gaps around a short link like "Email".
const NoteLink = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick: () => void;
}) => (
  <Button
    color="brand.500"
    fontSize="ah-status"
    fontWeight={700}
    h="auto"
    lineHeight="inherit"
    minW={0}
    px={0}
    variant="link"
    verticalAlign="baseline"
    onClick={onClick}
  >
    {children}
  </Button>
);

type Props = {
  platform: PlatformTypes | null;
  isWeblink: boolean;
  onOpenFeed: () => void;
  /** Omitted when Email isn't among the destinations this workspace offers. */
  onSelectEmail?: () => void;
};

export const DestinationDescription = ({
  platform,
  isWeblink,
  onOpenFeed,
  onSelectEmail,
}: Props) => {
  if (isWeblink) {
    return (
      <Note>
        Opens each event in {platform ? getName(platform) : 'your calendar'} to
        add it. Since it is a copy, it won’t update if the schedule changes. For
        automatic updates, subscribe to the{' '}
        <NoteLink onClick={onOpenFeed}>Calendar Feed</NoteLink>
        {onSelectEmail && (
          <>
            {' '}
            or get a Calendar Invite via{' '}
            <NoteLink onClick={onSelectEmail}>Email</NoteLink>
          </>
        )}
        .
      </Note>
    );
  }

  const module = platform ? getDestination(platform) : undefined;
  if (!module?.description) return null;
  const { description, syncKind } = module;

  return (
    <Flex align="center" flexWrap="wrap" gap={2} mt={3}>
      {syncKind && (
        <Text
          bg={SYNC_STYLE[syncKind].bg}
          borderRadius="ah-pill"
          color={SYNC_STYLE[syncKind].color}
          flexShrink={0}
          fontSize="ah-label"
          fontWeight={800}
          letterSpacing="0.04em"
          px={2.5}
          py={0.5}
          textTransform="uppercase"
        >
          {SYNC_STYLE[syncKind].label(
            module.name,
            module.syncTagPreposition ?? 'to'
          )}
        </Text>
      )}
      <Text color="ink.muted" fontSize="ah-status" lineHeight={1.45}>
        {description}
      </Text>
    </Flex>
  );
};
